import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Book, ArrowLeft, Check, Sparkle, Image as ImageIcon, X, Wrench } from '@phosphor-icons/react';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import GlobalHeader from '../components/GlobalHeader';

const categories = ['Poetry', 'Story', 'Shayari', 'Article', 'Blog', 'Short Story', 'Memoir'];

export const Editor = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [grammarSuggestion, setGrammarSuggestion] = useState('');
  const [showGrammar, setShowGrammar] = useState(false);
  const featuredImageInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'Poetry',
    tags: [],
    featured_image: '',
    meta_description: ''
  });

  useEffect(() => {
    if (articleId && articleId !== 'new') {
      const fetchArticle = async () => {
        try {
          setLoading(true);
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/articles/${articleId}`,
            { withCredentials: true }
          );
          const article = response.data;
          setFormData({
            title: article.title,
            content: article.content,
            category: article.category,
            tags: article.tags,
            featured_image: article.featured_image,
            meta_description: article.meta_description
          });
        } catch (error) {
          console.error('Error fetching article:', error);
          toast.error('Failed to load article');
        } finally {
          setLoading(false);
        }
      };
      fetchArticle();
    }
  }, [articleId]);

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      setUploading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/upload/image`,
        uploadFormData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      const path = res.data.url;
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;
      setFormData({ ...formData, featured_image: imageUrl });
      toast.success('Featured image uploaded');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (publish = false) => {
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }

    try {
      setSaving(true);
      
      if (articleId && articleId !== 'new') {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/articles/${articleId}`,
          formData,
          { withCredentials: true }
        );
        
        if (publish) {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/articles/${articleId}/publish`,
            {},
            { withCredentials: true }
          );
          toast.success('Article submitted for review');
          navigate('/dashboard');
        } else {
          toast.success('Article saved');
        }
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/articles`,
          formData,
          { withCredentials: true }
        );
        
        const newArticleId = response.data.article_id;
        
        if (publish) {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/articles/${newArticleId}/publish`,
            {},
            { withCredentials: true }
          );
          toast.success('Article submitted for review');
          navigate('/dashboard');
        } else {
          toast.success('Article saved as draft');
          navigate(`/editor/${newArticleId}`);
        }
      }
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateTitle = async () => {
    // Strip HTML for AI
    const textContent = formData.content.replace(/<[^>]*>/g, '');
    if (!textContent.trim()) {
      toast.error('Write some content first');
      return;
    }

    try {
      setAiLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-title`,
        { text: textContent },
        { withCredentials: true }
      );
      setFormData({ ...formData, title: response.data.title });
      toast.success('Title generated');
    } catch (error) {
      console.error('Error generating title:', error);
      toast.error('Failed to generate title');
    } finally {
      setAiLoading(false);
    }
  };

  const handleGenerateTags = async () => {
    const textContent = formData.content.replace(/<[^>]*>/g, '');
    if (!textContent.trim()) {
      toast.error('Write some content first');
      return;
    }

    try {
      setAiLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-tags`,
        { text: textContent },
        { withCredentials: true }
      );
      setFormData({ ...formData, tags: response.data.tags });
      toast.success('Tags generated');
    } catch (error) {
      console.error('Error generating tags:', error);
      toast.error('Failed to generate tags');
    } finally {
      setAiLoading(false);
    }
  };

  const handleGrammarCheck = async () => {
    const textContent = formData.content.replace(/<[^>]*>/g, '');
    if (!textContent.trim()) {
      toast.error('Write some content first');
      return;
    }
    try {
      setAiLoading(true);
      setShowGrammar(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/grammar-check`,
        { text: textContent.substring(0, 3000) },
        { withCredentials: true }
      );
      setGrammarSuggestion(response.data.result);
    } catch (error) {
      toast.error('Grammar check failed');
      setShowGrammar(false);
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const wordCount = formData.content.replace(/<[^>]*>/g, '').split(' ').filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Featured Image */}
          <div className="mb-8">
            <label className="block text-sm font-semibold mb-2 uppercase tracking-widest text-muted-foreground">Featured Image</label>
            {formData.featured_image ? (
              <div className="relative rounded-lg overflow-hidden aspect-video">
                <img src={formData.featured_image} alt="Featured" className="w-full h-full object-cover" data-testid="featured-image-preview" />
                <button
                  onClick={() => setFormData({ ...formData, featured_image: '' })}
                  data-testid="remove-featured-image"
                  className="absolute top-3 right-3 bg-black/70 text-white rounded-full p-2 hover:bg-black transition-colors"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => featuredImageInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-featured-image"
                className="w-full aspect-video border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-muted/50 transition-colors disabled:opacity-50"
              >
                <ImageIcon size={40} weight="duotone" className="text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{uploading ? 'Uploading...' : 'Click to upload featured image'}</p>
              </button>
            )}
            <input
              ref={featuredImageInputRef}
              type="file"
              accept="image/*"
              onChange={handleFeaturedImageUpload}
              className="hidden"
            />
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <input
                type="text"
                placeholder="Article Title..."
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                data-testid="article-title-input"
                className="flex-1 text-4xl md:text-5xl bg-transparent border-none outline-none tracking-tight"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}
              />
              <button
                onClick={handleGenerateTitle}
                disabled={aiLoading}
                data-testid="generate-title-button"
                className="text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
                title="Generate title with AI"
              >
                <Sparkle size={24} weight="duotone" />
              </button>
            </div>
          </div>

          <div className="mb-8 pb-8 border-b border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  data-testid="category-select"
                  className="w-full px-4 py-2 bg-card border border-border rounded-lg outline-none focus:border-primary"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Tags</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add tags (comma separated)"
                    value={formData.tags.join(', ')}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                    data-testid="tags-input"
                    className="flex-1 px-4 py-2 bg-card border border-border rounded-lg outline-none focus:border-primary"
                  />
                  <button
                    onClick={handleGenerateTags}
                    disabled={aiLoading}
                    data-testid="generate-tags-button"
                    className="text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
                    title="Generate tags with AI"
                  >
                    <Sparkle size={24} weight="duotone" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Rich Text Editor */}
          <div className="mb-3">
            <label className="block text-sm font-semibold mb-3">Content</label>
            <RichTextEditor
              content={formData.content}
              onChange={(html) => setFormData({ ...formData, content: html })}
            />
          </div>
          <p className="text-sm text-muted-foreground mb-8">
            {wordCount} words · {Math.ceil(wordCount / 200)} min read
          </p>

          <div className="mb-8">
            <label className="block text-sm font-semibold mb-2">Meta Description (SEO)</label>
            <textarea
              placeholder="Brief description for search engines..."
              value={formData.meta_description}
              onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
              data-testid="meta-description-input"
              className="w-full h-20 px-4 py-2 bg-card border border-border rounded-lg outline-none focus:border-primary resize-none"
            />
          </div>
        </div>
      </div>

      {/* Grammar Check Modal */}
      {showGrammar && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowGrammar(false)}>
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="grammar-modal">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-2xl flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                <Wrench size={24} weight="duotone" className="text-primary" />
                Grammar & Style Suggestions
              </h3>
              <button onClick={() => setShowGrammar(false)} data-testid="close-grammar-modal" className="p-2 hover:bg-muted rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {aiLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="prose max-w-none" data-testid="grammar-result">
                  <p className="whitespace-pre-wrap">{grammarSuggestion}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Editor;
